import React from "react";
import { FeatherDownload } from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import {
  CashBankPaginationEntity,
  CashBankSortField,
  CashBankSortOrder,
  CashBankTransactionEntity,
  CashBankTransactionStatus,
  CashBankTransactionType,
} from "../../domain";
import {
  CashBankSortState,
  CashBankTransactionToolbar,
} from "./CashBankTransactionToolbar";
import { CashBankTransactionTable } from "./CashBankTransactionTable";

interface CashBankTransactionSectionProps {
  title: string;
  type: CashBankTransactionType;
  status: CashBankTransactionStatus;
  onStatusChange: (value: CashBankTransactionStatus) => void;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  sort: CashBankSortState;
  onSortChange: (
    sortBy: CashBankSortField,
    sortOrder: CashBankSortOrder,
    label: string,
  ) => void;
  transactions: CashBankTransactionEntity[];
  pagination: CashBankPaginationEntity | null;
  loading: boolean;
  tableLoading?: boolean;
  error: string | null;
  canMatch: boolean;
  canUnmatch: boolean;
  canSplit: boolean;
  onRefresh: () => void;
  onReview: (transaction: CashBankTransactionEntity) => void;
  onSplit: (transaction: CashBankTransactionEntity) => void;
  onView: (transaction: CashBankTransactionEntity) => void;
  onPageChange: (page: number) => void;
}

export const CashBankTransactionSection = React.forwardRef<
  HTMLDivElement,
  CashBankTransactionSectionProps
>(
  (
    {
      title,
      type,
      status,
      onStatusChange,
      searchTerm,
      onSearchTermChange,
      sort,
      onSortChange,
      transactions,
      pagination,
      loading,
      tableLoading = loading,
      error,
      canMatch,
      canUnmatch,
      canSplit,
      onRefresh,
      onReview,
      onSplit,
      onView,
      onPageChange,
    },
    ref,
  ) => (
    <div ref={ref} className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full items-center justify-between">
        <span className="text-heading-3 font-heading-3 text-default-font">
          {title}
        </span>
      </div>
      <CashBankTransactionToolbar
        status={status}
        onStatusChange={onStatusChange}
        searchTerm={searchTerm}
        onSearchTermChange={onSearchTermChange}
        sort={sort}
        onSortChange={onSortChange}
        onRefresh={onRefresh}
        disabled={loading}
      />
      <div className="hidden w-full items-center justify-between rounded-md border border-solid border-brand-primary bg-brand-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <span className="text-body-bold font-body-bold text-brand-primary">
            3 transactions selected
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="small" onClick={() => {}}>
            Accept All
          </Button>
          <Button variant="neutral-tertiary" size="small" onClick={() => {}}>
            Reject All
          </Button>
          <Button
            variant="neutral-tertiary"
            size="small"
            icon={<FeatherDownload />}
            onClick={() => {}}
          >
            Export Selected
          </Button>
        </div>
      </div>
      <CashBankTransactionTable
        type={type}
        transactions={transactions}
        pagination={pagination}
        loading={tableLoading}
        inlineLoading={loading && !tableLoading}
        error={error}
        searchTerm={searchTerm}
        canMatch={canMatch}
        canUnmatch={canUnmatch}
        canSplit={canSplit}
        onReview={onReview}
        onSplit={onSplit}
        onView={onView}
        onRetry={onRefresh}
        onPageChange={onPageChange}
      />
    </div>
  ),
);

CashBankTransactionSection.displayName = "CashBankTransactionSection";
